import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { ApiError, api } from "../api/client";

export type ResolutionStatus = "pending" | "in_progress" | "resolved" | "false_alarm";

const STATUS_LABEL: Record<ResolutionStatus, string> = {
  pending: "Pending",
  in_progress: "In progress",
  resolved: "Resolved",
  false_alarm: "False alarm",
};

const STATUS_COLOR: Record<ResolutionStatus, string> = {
  pending: "#d97706",
  in_progress: "#0891b2",
  resolved: "#16a34a",
  false_alarm: "#64748b",
};

/**
 * Drop-in panel for any operational record (case, intel, POI, perf, pop).
 * Shows the current resolutionStatus + a button to flip it. Encrypts the
 * notes field on the way to the server. Caller passes which collection +
 * record id to act against.
 */
export function ResolutionPanel({
  collection,
  recordId,
  current,
  onResolved,
}: {
  collection: "cases" | "intelItems" | "peopleOfInterest" | "performanceReports" | "popularityReports";
  recordId: string;
  current?: ResolutionStatus | null;
  onResolved?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<ResolutionStatus>("resolved");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const resolve = useMutation({
    mutationFn: () =>
      api<{ id: string; resolutionStatus: ResolutionStatus }>(
        `/resolve/${collection}/${recordId}`,
        { method: "POST", body: JSON.stringify({ status, notes }) },
      ),
    onSuccess: () => {
      setOpen(false); setNotes(""); setError(null);
      onResolved?.();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const cur = current ?? "pending";

  return (
    <div className="card" style={{ marginTop: 16, borderLeft: `4px solid ${STATUS_COLOR[cur]}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 12, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
            Resolution
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              fontSize: 12, padding: "2px 10px", borderRadius: 999,
              background: STATUS_COLOR[cur] + "22", color: STATUS_COLOR[cur], fontWeight: 600,
            }}>{STATUS_LABEL[cur]}</span>
            {(cur === "pending" || cur === "in_progress") && (
              <span className="muted" style={{ fontSize: 12 }}>Action this record from below.</span>
            )}
            {cur === "resolved" && (
              <span className="muted" style={{ fontSize: 12 }}>This record is closed out — it no longer triggers notifications.</span>
            )}
          </div>
        </div>
        {!open && (cur === "pending" || cur === "in_progress") && (
          <button onClick={() => setOpen(true)}>Resolve</button>
        )}
        {open && <button className="ghost" onClick={() => setOpen(false)}>Cancel</button>}
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          <div className="row-3">
            <label>
              New status
              <select value={status} onChange={(e) => setStatus(e.target.value as ResolutionStatus)}>
                <option value="in_progress">In progress (work started)</option>
                <option value="resolved">Resolved (done — explain how below)</option>
                <option value="false_alarm">False alarm (no action needed)</option>
              </select>
            </label>
            <span /><span />
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            style={{ width: "100%", marginTop: 8 }}
            placeholder="What was done? Who acted? What was the outcome? Encrypted at rest."
          />
          {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              onClick={() => {
                if (window.confirm(`Mark this record as ${STATUS_LABEL[status]}? The audit log keeps the trail.`))
                  resolve.mutate();
              }}
              disabled={resolve.isPending}
            >
              {resolve.isPending ? "Saving…" : "Save resolution"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
